import React, { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ExternalLink,
  Store,
  Bell,
  TrendingDown,
  Clock,
  Loader2,
  AlertCircle,
  CheckCircle2,
  Tag,
} from 'lucide-react';
import { Modal, Button, StatusBadge } from '../../components/common';
import type { WatchlistViewItem } from './types';
import { apiClient } from '../../api/client';
import { formatCurrencyPrice, getCurrencySymbol } from '../../utils/currency';
import { PriceHistoryChart, type PriceHistoryPoint } from '../history/PriceHistoryChart';
import { calculatePriceStats, type ObservationItem } from '../history/stats';
import { PriceStatsCards } from '../history/PriceStatsCards';
import { type TimeRange } from '../history/RangeSelector';
import { useLanguage } from '../../contexts/LanguageContext';

export interface ProductDetailModalProps {
  watch: WatchlistViewItem | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const ProductDetailModal: React.FC<ProductDetailModalProps> = ({
  watch,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const { t } = useLanguage();
  const queryClient = useQueryClient();
  const [range, setRange] = useState<TimeRange>('30d');

  // Alert configuration local state
  const [targetPrice, setTargetPrice] = useState<string>('');
  const [notifyOnAnyDrop, setNotifyOnAnyDrop] = useState<boolean>(false);
  const [selectedDropPct, setSelectedDropPct] = useState<number | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState<boolean>(false);

  // Sync state when watch opens
  useEffect(() => {
    if (watch) {
      const existingTarget = watch.alertRule?.targetPrice ? String(watch.alertRule.targetPrice) : '';
      setTargetPrice(existingTarget);
      setNotifyOnAnyDrop(watch.alertRule?.notifyOnAnyDrop ?? false);
      setSelectedDropPct(null);
      setFormError(null);
      setSaveSuccess(false);
    }
  }, [watch, isOpen]);

  // Query price history observations
  const { data: historyData, isLoading: isHistoryLoading } = useQuery({
    queryKey: ['watch-history', watch?.id, range],
    queryFn: async () => {
      if (!watch) return null;
      const { data, error } = await apiClient.GET('/api/v1/watches/{id}/history', {
        params: {
          path: { id: watch.id },
          query: { range },
        },
      });
      if (error) throw error;
      return data;
    },
    enabled: isOpen && !!watch?.id,
  });

  // Map API points to local ObservationItem format
  const observations: ObservationItem[] = useMemo(() => {
    if (!historyData?.points) return [];
    return historyData.points.map((pt) => ({
      price: pt.price,
      list_price: pt.list_price,
      currency: (pt as any).currency || historyData.currency || watch?.establishedCurrency || '',
      recorded_at: pt.timestamp,
    }));
  }, [historyData, watch?.establishedCurrency]);

  const stats = useMemo(() => calculatePriceStats(observations), [observations]);

  const chartPoints: PriceHistoryPoint[] = useMemo(() => {
    return observations.map((o) => ({
      price: o.price,
      list_price: o.list_price,
      recorded_at: o.recorded_at,
      currency: o.currency,
    }));
  }, [observations]);

  // Handle Preset Drop Percentage Selection
  const handleSelectPreset = (pct: number | 'any') => {
    setSaveSuccess(false);
    if (pct === 'any') {
      setNotifyOnAnyDrop(true);
      setSelectedDropPct(0);
      setTargetPrice('');
    } else {
      setNotifyOnAnyDrop(false);
      setSelectedDropPct(pct);
      const current = parseFloat(watch?.currentPrice || '0');
      if (current > 0) {
        const calculated = (current * (1 - pct / 100)).toFixed(2);
        setTargetPrice(calculated);
      }
    }
  };

  // Upsert Alert Rule Mutation
  const saveAlertMutation = useMutation({
    mutationFn: async () => {
      if (!watch) return;
      setFormError(null);
      setSaveSuccess(false);

      const trimmed = targetPrice.trim();
      if (trimmed && isNaN(Number(trimmed))) {
        throw new Error('Please enter a valid target price number.');
      }
      if (trimmed && Number(trimmed) <= 0) {
        throw new Error('Target price must be greater than zero.');
      }

      const { data, error } = await apiClient.PUT('/api/v1/watches/{id}/alert', {
        params: { path: { id: watch.id } },
        body: {
          target_price: trimmed ? trimmed : null,
          target_currency: watch.establishedCurrency,
          notify_on_any_drop: notifyOnAnyDrop,
          min_drop_pct: selectedDropPct ?? (Number(watch.alertRule?.minDropPct) || 3.0),
          min_drop_abs: 1.0,
          cooldown_seconds: 600,
          is_active: true,
        },
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      setSaveSuccess(true);
      queryClient.invalidateQueries({ queryKey: ['watches'] });
      if (onSuccess) onSuccess();
      setTimeout(() => setSaveSuccess(false), 3000);
    },
    onError: (err: any) => {
      setFormError(err?.message || 'Failed to save alert rule.');
    },
  });

  if (!watch) return null;

  const currentPriceFormatted = formatCurrencyPrice(watch.currentPrice, watch.establishedCurrency);
  const listPriceFormatted = watch.listPrice
    ? formatCurrencyPrice(watch.listPrice, watch.establishedCurrency)
    : null;
  const currencySymbol = getCurrencySymbol(watch.establishedCurrency);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      maxWidth="2xl"
      title={
        <div className="flex items-center justify-between gap-3 pe-6 w-full">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300">
              <Store className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />
              <span>{watch.merchantName || watch.merchantDomain}</span>
              {watch.storefrontContext?.country_code && (
                <span className="text-[9px] uppercase font-bold px-1.5 py-0.5 bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-300 rounded border border-slate-200 dark:border-slate-600">
                  {watch.storefrontContext.country_code}
                </span>
              )}
            </span>
            {watch.trackabilityStatus === 'GEO_MISMATCH' ? (
              <span
                data-testid="modal-status-held"
                className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-amber-50 dark:bg-amber-950/60 text-amber-600 dark:text-amber-400 border border-amber-200 dark:border-amber-800/60"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                <span>HELD</span>
              </span>
            ) : watch.trackabilityStatus === 'UNSUPPORTED' || watch.trackabilityStatus === 'BLOCKED' ? (
              <span
                data-testid="modal-status-unsupported"
                className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-red-50 dark:bg-red-950/60 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800/60"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
                <span>UNSUPPORTED</span>
              </span>
            ) : watch.trackabilityStatus === 'DEGRADED' ? (
              <span
                data-testid="modal-status-degraded"
                className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-orange-50 dark:bg-orange-950/60 text-orange-600 dark:text-orange-400 border border-orange-200 dark:border-orange-800/60"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-orange-500" />
                <span>DEGRADED</span>
              </span>
            ) : (
              <StatusBadge status={watch.isActive ? 'ACTIVE' : 'PAUSED'} />
            )}
          </div>
          {watch.requestedUrl && (
            <a
              href={watch.requestedUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600 dark:text-emerald-400 hover:underline"
            >
              <span>{t('modal.storeLink')}</span>
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          )}
        </div>
      }
    >
      <div className="space-y-6 pt-1">
        {watch.trackabilityStatus === 'GEO_MISMATCH' && (
          <div
            data-testid="modal-held-warning"
            className="p-3.5 rounded-xl bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800/60 text-amber-800 dark:text-amber-300 text-xs flex items-start gap-2.5"
          >
            <AlertCircle className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold">Tracking Held for Storefront Mismatch</p>
              <p className="text-amber-700 dark:text-amber-400 mt-0.5">
                {watch.lastFailureCode
                  ? `Reason: ${watch.lastFailureCode}. Scheduled price updates are held because the detected storefront does not match this track's regional market context.`
                  : "Scheduled price updates are held because the detected storefront does not match this track's regional market context."}
              </p>
            </div>
          </div>
        )}

        {/* Product Title & Current Pricing Header */}
        <div className="border-b border-slate-100 dark:border-slate-800 pb-4">
          <h2 className="text-base font-bold text-slate-900 dark:text-slate-100 leading-snug mb-3">
            {watch.productTitle}
          </h2>

          <div className="flex flex-wrap items-baseline gap-3">
            <div className="text-2xl font-black text-slate-900 dark:text-slate-100 tracking-tight">
              {currentPriceFormatted}
            </div>

            {listPriceFormatted && (
              <div className="text-sm font-medium text-slate-400 line-through">
                {listPriceFormatted}
              </div>
            )}

            {watch.discountPct !== null && watch.discountPct > 0 && (
              <span className="inline-flex items-center gap-1 text-xs font-bold text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-950/60 px-2 py-0.5 rounded-full">
                <Tag className="w-3 h-3" />
                {watch.discountPct.toFixed(0)}% OFF
              </span>
            )}
          </div>
        </div>

        {/* Horizontal Price History Chart Section */}
        <div>
          <div className="flex items-center justify-between gap-2 mb-3">
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300 flex items-center gap-1.5">
              <TrendingDown className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
              <span>{t('modal.priceHistory')}</span>
            </h3>

            {/* Date Range Selector Buttons */}
            <div className="inline-flex items-center p-0.5 rounded-lg bg-slate-100 dark:bg-slate-800 text-xs font-medium text-slate-600 dark:text-slate-300">
              <button
                type="button"
                onClick={() => setRange('30d')}
                className={`px-2.5 py-1 rounded-md transition-colors ${
                  range === '30d'
                    ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 shadow-xs font-semibold'
                    : 'hover:text-slate-900 dark:hover:text-slate-100'
                }`}
              >
                {t('modal.range30d')}
              </button>
              <button
                type="button"
                onClick={() => setRange('90d')}
                className={`px-2.5 py-1 rounded-md transition-colors ${
                  range === '90d'
                    ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 shadow-xs font-semibold'
                    : 'hover:text-slate-900 dark:hover:text-slate-100'
                }`}
              >
                {t('modal.range90d')}
              </button>
              <button
                type="button"
                onClick={() => setRange('all')}
                className={`px-2.5 py-1 rounded-md transition-colors ${
                  range === 'all'
                    ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 shadow-xs font-semibold'
                    : 'hover:text-slate-900 dark:hover:text-slate-100'
                }`}
              >
                {t('modal.rangeAll')}
              </button>
            </div>
          </div>

          {/* Chart & Stats Container */}
          {isHistoryLoading ? (
            <div className="py-16 flex flex-col items-center justify-center gap-2 text-slate-400 bg-slate-50 dark:bg-slate-800/50 rounded-xl">
              <Loader2 className="w-6 h-6 animate-spin text-emerald-600" />
              <p className="text-xs">Loading price observations...</p>
            </div>
          ) : observations.length > 0 ? (
            <div className="space-y-3">
              <PriceStatsCards stats={stats} currency={watch.establishedCurrency} />
              <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-3 shadow-xs">
                <PriceHistoryChart
                  points={chartPoints}
                  currency={watch.establishedCurrency}
                  targetPrice={watch.alertRule?.targetPrice}
                />
              </div>
            </div>
          ) : (
            <div className="py-12 text-center bg-slate-50 dark:bg-slate-800/40 rounded-xl border border-dashed border-slate-200 dark:border-slate-800">
              <Clock className="w-8 h-8 text-slate-400 mx-auto mb-2" />
              <p className="text-xs text-slate-500 dark:text-slate-400">{t('modal.noHistory')}</p>
            </div>
          )}
        </div>

        {/* Alert Rule & Target Price Configuration Section */}
        <div className="bg-slate-50 dark:bg-slate-800/40 border border-slate-200/80 dark:border-slate-800 rounded-xl p-4 space-y-4">
          <div className="flex items-center gap-2">
            <Bell className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300">
              {t('modal.alertSettings')}
            </h3>
          </div>

          {/* Quick Percentage Presets */}
          <div>
            <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-2">
              {t('modal.dropPercentage')}
            </label>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => handleSelectPreset('any')}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                  notifyOnAnyDrop
                    ? 'bg-emerald-600 text-white shadow-xs font-semibold'
                    : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700 hover:border-emerald-500'
                }`}
              >
                {t('modal.anyDrop')}
              </button>
              {[5, 10, 15, 20, 25].map((pct) => (
                <button
                  key={pct}
                  type="button"
                  onClick={() => handleSelectPreset(pct)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                    selectedDropPct === pct && !notifyOnAnyDrop
                      ? 'bg-emerald-600 text-white shadow-xs font-semibold'
                      : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700 hover:border-emerald-500'
                  }`}
                >
                  {pct}% Drop
                </button>
              ))}
            </div>
          </div>

          {/* Explicit Target Price Input */}
          <div>
            <label
              htmlFor="target-price-input"
              className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1.5"
            >
              {t('modal.targetPrice')} ({watch.establishedCurrency})
            </label>
            <div className="relative max-w-xs">
              <div className="absolute inset-y-0 start-0 ps-3 flex items-center pointer-events-none text-slate-400 text-xs font-bold">
                {currencySymbol}
              </div>
              <input
                id="target-price-input"
                type="text"
                value={targetPrice}
                onChange={(e) => {
                  setTargetPrice(e.target.value);
                  setNotifyOnAnyDrop(false);
                  setSelectedDropPct(null);
                  if (formError) setFormError(null);
                }}
                placeholder="e.g. 299.00"
                className="w-full ps-10 pe-4 py-2 text-sm rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
              />
            </div>
          </div>

          {/* Inline Feedback / Errors */}
          {formError && (
            <div className="flex items-center gap-1.5 text-xs text-rose-600 dark:text-rose-400">
              <AlertCircle className="w-4 h-4 shrink-0" />
              <span>{formError}</span>
            </div>
          )}

          {saveSuccess && (
            <div className="flex items-center gap-1.5 text-xs text-emerald-700 dark:text-emerald-300">
              <CheckCircle2 className="w-4 h-4 shrink-0" />
              <span>{t('modal.saveSuccess')}</span>
            </div>
          )}

          {/* Save Action */}
          <div className="flex justify-end pt-2">
            <Button
              variant="primary"
              size="sm"
              isLoading={saveAlertMutation.isPending}
              onClick={() => saveAlertMutation.mutate()}
              className="gap-1.5"
            >
              <Bell className="w-3.5 h-3.5" />
              <span>{saveAlertMutation.isPending ? t('modal.saving') : t('modal.saveAlert')}</span>
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};
